import { Contract as w3Contract } from "@/lib/web3x-es/contract/contract";
import { WebsocketProvider } from "@/lib/web3x-es/providers";
import { ContractAbi } from "@/lib/web3x-es/contract/abi";
import { bufferToHex } from "@/lib/web3x-es/utils";
import { Address } from "@/lib/web3x-es/address";
import { Eth } from "@/lib/web3x-es/eth";

import { randomBytes } from "crypto";
import { soliditySha3 } from "@/lib/web3x-es/utils/solidity-sha3";

const CONFIGURED_NETWORK = process.env.VUE_APP_WEB3_NETWORK || "";
const READ_ONLY_PROVIDER = process.env.VUE_APP_CHILDCHAIN_PROVIDER || "";

// TODO: change
const MATIC_TSX: any = {
  goerli: "0x1ce8614e6e7FF90A5874CD04e8922c412EEd4a4b", // (POS MUMBAI TSX)
  mainnet: "0xF11de26883EB2EBF9877a01703DDC1eB619094E6", // (POS MATIC TSX)
};

export const reserveTokenAddress = MATIC_TSX[CONFIGURED_NETWORK];

export abstract class Contract<T = any> {
  protected instance: any = null;

  private address = "";
  private name = "";
  private abi: any = null;

  public constructor(artifact: any, address?: string) {
    this.setAddress(address || artifact[CONFIGURED_NETWORK]);
    this.setName(artifact.name);
    this.setAbi(artifact.abi);
  }

  public initInstance(ethInstance: any) {
    if (this.instance) {
      console.debug("contract already initialized: ", this.getName());
    } else {
      const address = Address.fromString(this.address);
      const contractAbi = new ContractAbi(this.abi);

      this.instance = new w3Contract(ethInstance, contractAbi, address);
    }
  }

  public initReadOnly(provider: string = READ_ONLY_PROVIDER) {
    const ws = new WebsocketProvider(provider, {
      timeout: 360000,
    });
    this.initInstance(new Eth(ws));
  }

  public encodeABI(method: string, ...params: any[]) {
    return bufferToHex(this.instance.methods[method](...params).encodeABI());
  }

  public decodeTxEvents(abi: any, rawTxLogs: any[]) {
    const contractAbi = new ContractAbi(abi);

    const events = contractAbi.events.map((x) => x.signature);
    const filteredTxLogs = rawTxLogs.filter((x) =>
      events.includes(x.topics[0])
    );

    return filteredTxLogs.map(contractAbi.decodeEvent, contractAbi);
  }

  public getName(): string {
    return this.name;
  }

  public getAddress(): string {
    return this.address;
  }

  /**
   * Create a executeRelayedTx
   * @param signerWallet
   * @param abiEncoded of the method to call relayed
   */
  public async executeRelayedTx(signerWallet: any, abiEncoded: string) {
    /// relay data

    const chainId = await signerWallet.getChainId();
    const nonce = `0x${randomBytes(32).toString("hex")}`; // create a random nonce;
    const signer = signerWallet.getAddress();

    const orderHash = soliditySha3(
      { type: "uint256", value: nonce },
      { type: "address", value: signer },
      { type: "bytes", value: abiEncoded },
      { type: "uint256", value: chainId }
    );

    // relay tx
    const orderHashSignature = signerWallet.sign(orderHash);

    return this.encodeABI(
      "executeRelayedTx",
      nonce,
      signer,
      abiEncoded,
      orderHashSignature.signature
    );
  }

  protected setName(name: string) {
    if (!name) {
      throw new Error("Tried to instantiate a Contract without a `name`");
    }
    this.name = name;
  }

  protected setAddress(address: string) {
    if (!address) {
      throw new Error("Tried to instantiate a Contract without an `address`");
    }
    this.address = address;
  }

  protected setAbi(abi: object) {
    if (!abi) {
      throw new Error("Tried to instantiate a Contract without a `json abi`");
    }
    this.abi = abi;
  }
}
